import {
	isRichTableCell,
	type ParsedTableCell,
	tableCellText,
} from "./parseTableElement";

type TableCellValue = string | ParsedTableCell;

/** The formats a single cell's text can look like. */
type CellFormat = "numeric" | "date" | "time" | "date-time";

/**
 * `numeric-column` is the column-level upgrade of `numeric`: a column of numbers that
 * all measure the same thing, and so can be right aligned as well as tabular.
 */
export type TableColumnFormat = CellFormat | "numeric-column";

export type CellClassification = {
	format: CellFormat;
	/** Unit a numeric cell carries, e.g. `%` or `s`; empty for a bare number. */
	suffix: string;
};

/** Share of the non-blank body cells in a column that must share one format. */
const COLUMN_FORMAT_THRESHOLD = 0.8;

/**
 * Plain integers, grouped thousands and decimals, with an optional sign, leading
 * currency symbol, and a trailing unit of up to three letters or a percent sign —
 * e.g. `42`, `-1,234.50`, `$1 200`, `12.5%`, `25.6s`.
 */
const NUMERIC =
	/^[+-]?[$£€¥]?(?:\d{1,3}(?:[,\u00a0\u202f ]\d{3})+|\d+)(?:\.\d+)?\s?(%|[a-z]{1,3})?$/i;

/** `2026-09-04`, `2026/09/04`, `04/09/2026`, `9.4.2026`. */
const DATE_PATTERN = String.raw`\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.]\d{4}`;

/** `14:47`, `14:47:00`, `14:47:00.123`, `2:47 pm`. */
const TIME_PATTERN = String.raw`\d{1,2}:\d{2}(?::\d{2})?(?:\.\d{1,3})?(?:\s?[ap]\.?m\.?)?`;

/** `Z`, `+01:00`, `-0500`. */
const ZONE_PATTERN = String.raw`Z|[+-]\d{2}:?\d{2}`;

const DATE = new RegExp(`^(?:${DATE_PATTERN})$`, "i");
const TIME = new RegExp(`^(?:${TIME_PATTERN})(?:${ZONE_PATTERN})?$`, "i");
const DATE_TIME = new RegExp(
	`^(?:${DATE_PATTERN})[T ](?:${TIME_PATTERN})(?:${ZONE_PATTERN})?$`,
	"i",
);

const cellText = (cell: TableCellValue | undefined): string => {
	if (cell === undefined) {
		return "";
	}
	if (isRichTableCell(cell)) {
		return cell.nodes.map((node) => node.textContent ?? "").join("");
	}
	return tableCellText(cell);
};

/** What a single cell's text looks like, or `undefined` if it looks like prose. */
export function classifyCell(text: string): CellClassification | undefined {
	// Accounting negatives, e.g. `(1,234.00)`.
	const value = text.trim().replace(/^\((.*)\)$/, "-$1");
	if (value === "") {
		return undefined;
	}
	if (DATE_TIME.test(value)) {
		return { format: "date-time", suffix: "" };
	}
	if (DATE.test(value)) {
		return { format: "date", suffix: "" };
	}
	if (TIME.test(value)) {
		return { format: "time", suffix: "" };
	}
	const numeric = NUMERIC.exec(value);
	if (!numeric) {
		return undefined;
	}
	return { format: "numeric", suffix: (numeric[1] ?? "").toLowerCase() };
}

/**
 * Formats the columns whose body cells all — or nearly all — share one format, so those
 * cells and their header can be styled with tabular figures. Blank cells are ignored,
 * and header text is never considered.
 */
export function detectColumnFormats(
	rows: TableCellValue[][],
	columnCount: number,
): (TableColumnFormat | undefined)[] {
	const valueCounts = new Array<number>(columnCount).fill(0);
	const formatCounts: Map<CellFormat, number>[] = Array.from(
		{ length: columnCount },
		() => new Map(),
	);
	const numericSuffixes: Set<string>[] = Array.from(
		{ length: columnCount },
		() => new Set(),
	);

	for (const row of rows) {
		for (let col = 0; col < columnCount; col++) {
			const text = cellText(row[col]).trim();
			if (text === "") {
				continue;
			}
			valueCounts[col]++;
			const classification = classifyCell(text);
			if (!classification) {
				continue;
			}
			const { format, suffix } = classification;
			formatCounts[col].set(format, (formatCounts[col].get(format) ?? 0) + 1);
			if (format === "numeric") {
				numericSuffixes[col].add(suffix);
			}
		}
	}

	return valueCounts.map((values, col) => {
		if (values === 0) {
			return undefined;
		}
		// The threshold is above half, so at most one format can reach it.
		for (const [format, count] of formatCounts[col]) {
			if (count / values < COLUMN_FORMAT_THRESHOLD) {
				continue;
			}
			if (format !== "numeric") {
				return format;
			}
			// Numbers are only right aligned when they all carry the same unit. A column
			// holding both `1.0%` and `25.6s` measures several things, so aligning its
			// digits would imply a comparison that isn't there.
			return numericSuffixes[col].size > 1 ? "numeric" : "numeric-column";
		}
		return undefined;
	});
}
